import { TUserAuthTokenPayload } from '@qapline/api';
import { TLocation } from '@qapline/core/location';
import { timestamp } from '@qapline/helpers';
import { T } from '@qapline/translations';
import i18n from '@qapline/translations/i18next';
import { differenceInDays, format, formatRelative, Locale } from 'date-fns';
import { format as formatDate } from 'date-fns';
import { cs, de } from 'date-fns/locale';
import { createFullName } from 'modules/carriers/utils';
import {
    CommissionDischargeSchema,
    CommissionGoodsSchema,
    CommissionLoadingSchema,
    CommissionSchema,
} from 'modules/commissions';
import { Countries, universalLanguage } from 'modules/common';
import { CustomerDetailSchema } from 'modules/customers';

// Stored timestamps are unix SECONDS; date-fns wants a Date (or milliseconds).
const toDateFnsDate = (value: unknown): Date => timestamp(value as number)?.toDate() ?? new Date(0);

export type LoadingWithLocation = CommissionLoadingSchema & TLocation & { company: string };
export type DischargeWithLocation = CommissionDischargeSchema & TLocation & { company: string };
import { TCustomerForm } from 'modules/form/components/CustomersBasicForm/customerForm.schema';
import { InvoiceFormCommissionSchema, InvoiceFormSchema, InvoicePreviewPdfRequestSchema } from 'modules/invoicing';
import { GeocoderSearchResult, GeocoderSearchResultType } from 'modules/onboarding/components/GeocoderPanel';
import { PaginationParams, QueryParams } from 'types/query-params';

import { getEarlyPayFactoringNotice } from './earlyPayEmail';

/**
 * Downloading the file.
 *
 * @param uri specifies the path to the file.
 * @param name new filename for the downloaded file.
 */
export function downloadURI(uri: string, name: string) {
    const link = document.createElement('a');
    document.body.appendChild(link);

    link.download = name;
    link.href = uri;
    link.click();
    link.parentNode && link.parentNode.removeChild(link);
}

/**
 * Printing the file.
 *
 * @param uri specifies the path to the file.
 */
export function printURI(uri: string) {
    const iframe = document.createElement('iframe');
    document.body.appendChild(iframe);

    iframe.style.display = 'none';
    iframe.src = uri;
    iframe.onload = function () {
        setTimeout(function () {
            iframe.focus();
            iframe.contentWindow?.print();
        }, 1);
    };
}

/**
 * Get the lowest order date across commissions.
 *
 * @param newCommissionsList new commissions list to get the order date from.
 * @return order date.
 */
export function getInvoiceOrderDate(newCommissionsList: InvoiceFormCommissionSchema[]): string {
    const allOrderDates = newCommissionsList.reduce<number[]>((acc, { orderDate }) => {
        if (orderDate) return [...acc, Number(orderDate)];
        return acc;
    }, []);
    const lowestOrderDate = allOrderDates.reduce((prev, cur) => {
        if (timestamp(cur)?.isBefore(timestamp(prev))) return cur;
        return prev;
    }, allOrderDates[0]);
    return String(lowestOrderDate);
}

/**
 * Get the lo point date across commissions.
 *
 * @param newCommissionsList new commissions list to get date point date from.
 * @return order date.
 */
export function getInvoicePointDate(newCommissionsList: InvoiceFormCommissionSchema[]): string {
    const allPointDates = newCommissionsList.reduce<number[]>((acc, { pointDate }) => {
        if (pointDate) return [...acc, pointDate];
        return acc;
    }, []);
    const highestPointDate = allPointDates.reduce((prev, cur) => {
        if (timestamp(cur)?.isAfter(timestamp(prev))) return cur;
        return prev;
    }, allPointDates[0]);
    return String(highestPointDate);
}

/**
 * Simplify creating the request body to one function.
 *
 * @param invoice invoice to get data from.
 * @param customer invoice customer.
 * @param user current authorized user.
 * @return body prepared for request.
 */
export function simplifyGetInvoicePdfRequestBody(
    invoice: InvoiceFormSchema,
    customer: Pick<
        CustomerDetailSchema,
        'street' | 'country' | 'postalCode' | 'city' | 'taxId' | 'place' | 'companyRegistrationNumber'
    >,
    user: TUserAuthTokenPayload,
): InvoicePreviewPdfRequestSchema {
    return {
        createdBy: createFullName(user.name, user.surname),
        createdByMail: user.email,
        customerCompany: invoice.customer_company,
        customerStreet: customer.street || customer.place?.street || '',
        customerCountry: customer.place?.countryCode ? i18n.t(`common.country.${customer.place.countryCode}`) : '',
        customerPostalCode: customer.postalCode || customer.place?.postalCode || '',
        customerCity: customer.city || customer.place?.city || '',
        registrationNumber: customer.companyRegistrationNumber?.toString() || '',
        currency: invoice.currency || 'CZK',
        taxId: customer.taxId?.toString() || '',
        language: invoice.language || 'cs',
        // the rateBase still will be recalculated in the server
        rateBase: invoice.currency == 'CZK' ? 1 : 24.5,
        varSymbol: invoice.number,
        constSymbol: invoice.constantSymbol || '',
        orderDate: format(toDateFnsDate(invoice.dateOrder || 0), 'dd.MM.yyyy'),
        exposureDate: format(toDateFnsDate(invoice.dateIssue), 'dd.MM.yyyy'),
        maturityDate: format(toDateFnsDate(invoice.dateDue), 'dd.MM.yyyy'),
        performanceDate: format(toDateFnsDate(invoice.datePoint), 'dd.MM.yyyy'),
        paymentMethod: 'order',
        commissions: invoice.commissions.map((item) => ({
            text: `${item.qid} (${item.loading_city_string} -> ${item.discharge_city_string} )`,
            orderedBy: String(item.commissionNumber),
            orderDate: format(toDateFnsDate(item.orderDate), 'dd.MM.yyyy'),
            loadingDate: format(toDateFnsDate(item.loading_date?.[0]), 'dd.MM.yyyy') || '',
            price: item.priceCustomer,
            vat: item.vat === '0' || item.vat === '21' ? Number(item.vat) : 21,
        })),
    };
}

/**
 * Merge the loading with appropriate location.
 * @param loadings array of loadings.
 * @param locations array of locations.
 * @return array of merged loadings and locations.
 */

export const mergeLoadingsWithLocations = (
    loadings: CommissionLoadingSchema[],
    locations: TLocation[],
): LoadingWithLocation[] => {
    const locationsByLocationId = locations.reduce<{ [id: number]: TLocation }>((acc, location) => {
        return { ...acc, [Number(location.location_id)]: location };
    }, {});

    return loadings.map((item) => {
        const location = locationsByLocationId[Number(item.location_id)];
        return {
            ...item,
            ...location,
            company: location?.name ?? '',
        };
    });
};

/**
 * Merge the discharges with appropriate location.
 * @param discharges array of discharges.
 * @param locations array of locations.
 * @return array of merged discharges and locations.
 */

export const mergeDischargesWithLocations = (
    discharges: CommissionDischargeSchema[],
    locations: TLocation[],
): DischargeWithLocation[] => {
    const locationsByLocationId = locations.reduce<{ [id: number]: TLocation }>((acc, location) => {
        return { ...acc, [Number(location.location_id)]: location };
    }, {});

    return discharges.map((item) => {
        const location = locationsByLocationId[Number(item.location_id)];
        return {
            ...item,
            ...location,
            company: location?.name ?? '',
        };
    });
};

export const phoneRegex = new RegExp(/(?:\(?\+\d{2}\)?\s*)?\d+(?:[ -]*\d+)*$/);
export const numberRegex = new RegExp(/^[0-9]*$/);

export function getMessageAndSubject({
    format,
    language,
    commissionNumber,
    loadings,
    discharges,
    goods,
    driverPhoneNumber,
    driverName,
    commission,
}: {
    format:
        | 'createOrderCarrier'
        | 'createOrderConfirmation'
        | 'createLoadingConfirmation'
        | 'createDeliveryConfirmation'
        | 'neutralization';
    language: 'cs' | 'de' | 'en';
    commissionNumber: string;
    loadings: (CommissionLoadingSchema & { city: string | null; street: string | null })[];
    discharges: (CommissionDischargeSchema & { city: string | null; street: string | null })[];
    goods: CommissionGoodsSchema[];
    driverPhoneNumber: string;
    driverName: string;
    commission: CommissionSchema;
}): { subject: string; message: string } {
    let { subject, message } = emailTemplates?.[format]?.[language] || {};

    const pricesByCurrencyAndLanguage: { cs: string; de: string; en: string } = {
        cs: commission.currencyCustomer === 'EUR' ? `,\n\t   ${commission.priceCustomer || 0},- EUR bez DPH.` : '.',
        de: commission.currencyCustomer === 'EUR' ? `,\n\t   ${commission.priceCustomer || 0},- EUR ohne MwSt.` : '.',
        en: commission.currencyCustomer === 'EUR' ? `,\n\t   ${commission.priceCustomer || 0},- EUR without VAT.` : '.',
    };

    // without the price or its rate there is nothing to convert, so the amount stays blank
    const priceInCZK =
        commission.currencyCustomer === 'EUR'
            ? commission.priceCustomer && commission.exchangeRateCustomer
                ? String((commission.priceCustomer * commission.exchangeRateCustomer).toFixed())
                : ''
            : String(commission.priceCustomer || '');

    subject = subject.replace('{commissionNumber}', commissionNumber);
    message = message.replace(/\n/g, '\n');
    message = message.replace('{commissionNumber}', commissionNumber);
    message = message.replace(
        '{loadingCities}',
        loadings
            .filter(({ city }) => city)
            .map(({ city }) => city)
            .join(' + '),
    );
    message = message.replace(
        '{dischargeCities}',
        discharges
            .filter(({ city }) => city)
            .map(({ city }) => city)
            .join(' + '),
    );
    message = message.replace('{goodsNames}', goods.map(({ name }) => name).join(', '));
    message = message.replace('{driverPhoneNumber}', driverPhoneNumber);
    message = message.replace('{driverName}', driverName);
    message = message.replace('{commissionPriceInCZK}', priceInCZK);
    message = message.replace('{rz}', String(commission.carrierRegistrationPlate));
    message = message.replace('{commissionPriceInEUR}', String(commission.priceCustomer));
    message = message.replace('{commissionPriceInEURFullText}', pricesByCurrencyAndLanguage[language]);
    message = message.replace(
        '{loadingDates}',
        loadings
            .map(
                ({ date, dateTo }) =>
                    `${formatDate(toDateFnsDate(date), 'dd.MM.yyyy')} ${
                        date === dateTo || !dateTo ? '' : `- ${formatDate(toDateFnsDate(dateTo), 'dd.MM.yyyy')}`
                    }`,
            )
            .join(' + '),
    );
    message = message.replace(
        '{dischargeDates}',
        discharges
            .map(
                ({ date, dateTo }) =>
                    `${formatDate(toDateFnsDate(date), 'dd.MM.yyyy')} ${
                        date === dateTo || !dateTo ? '' : `- ${formatDate(toDateFnsDate(dateTo), 'dd.MM.yyyy')}`
                    }`,
            )
            .join(' + '),
    );
    message = message.replace('{goodsWeights}', goods.map(({ weight }) => weight).join(', '));
    message = message.replace('{goodsPackaging}', goods.map(({ packaging }) => packaging).join(', '));
    message = message.replace('{goodsLoadingMeters}', goods.map(({ loadingMeters }) => loadingMeters).join(', '));

    return { subject, message };
}

export const emailTemplates = {
    createOrderCarrier: {
        cs: {
            subject: 'Objednávka přepravy',
            message: `Dobrý den,\n\nv\u00a0příloze Vám posílám objednávku přepravy, kterou evidujeme pod číslem {commissionNumber}.\n\nProsím o potvrzení přijetí.\n\n${getEarlyPayFactoringNotice('cs')}\n\nFakturu a dodací listy prosím nahrajte zde:\n{attachmentsUploadLink}\n\nDěkuji\nS\u00a0přáním hezkého dne`,
        },
        de: {
            subject: 'Transportbestellung',
            message: `Guten Tag,\n\nbeifügend  schicke ich Ihnen die TransportBestellung.\nIch bitte um die Bestätigung.\n\n${getEarlyPayFactoringNotice('de')}\n\nBitte laden Sie die Rechnung und die Lieferscheine hier hoch:\n{attachmentsUploadLink}\n\nVielen Dank\nMit freudlichen Grüßen`,
        },
        en: {
            subject: 'Transport order',
            message: `Hello,\n\nAttached please find a transportation order.\nPlease confirm receipt.\n\n${getEarlyPayFactoringNotice('en')}\n\nPlease upload the invoice and delivery notes here:\n{attachmentsUploadLink}\n\nThank you in advance.\n\nBest regards,`,
        },
    },
    createOrderConfirmation: {
        cs: {
            subject: 'Potvrzení objednávky {commissionNumber}',
            message: `Dobrý den,\n\npotvrzujeme přepravu na trase {loadingCities} -> {dischargeCities} evidovanou pod {commissionNumber}.\n\nTermín nakládky: {loadingDates}\nTermín vykládky: {dischargeDates}\nZboží: {goodsNames}\nHmotnost: {goodsWeights} kg\nZpůsob balení: {goodsPackaging}\nRozměr: {goodsLoadingMeters}\n\nRZ vozidla: {rz}\nŘidič: {driverName}, Tel.: {driverPhoneNumber}\nCena: {commissionPriceInCZK},- CZK bez DPH{commissionPriceInEURFullText}\n\nŽádáme Vás o závazné potvrzení Vaší objednávky.\n\nS přáním pěkného dne`,
        },
        de: {
            subject: 'Bestätigung der Bestellung',
            message: `Guten Tag,\n\nHiermit bestätigen wir den Transport aus {loadingCities} -> {dischargeCities}, die wir unter {commissionNumber} registrieren.\n\nAufladung: {loadingDates}\nEntladung: {dischargeDates}\nWare: {goodsNames}\nPreis: {commissionPriceInCZK},- CZK ohne MwSt{commissionPriceInEURFullText}\n\nBitte bestätigen Sie Ihre Bestellung`,
        },
        en: {
            subject: 'Order confirmation',
            message: `Hello,\n\nI confirm the transportation from {loadingCities} -> {dischargeCities}. Which we register under {commissionNumber}.\n\nLoading date: {loadingDates}\nDelivery date: {dischargeDates}\nGoods: {goodsNames}\nPrice: {commissionPriceInCZK},- CZK without VAT{commissionPriceInEURFullText}\n\nPlease, confirm by return.`,
        },
    },
    createLoadingConfirmation: {
        cs: {
            subject: 'Informace k nakládce',
            message: 'Dobrý den,\n\nv příloze Vám posílám avizaci nakládky u Vás ve firmě.\n\nS přáním pěkného dne',
        },
        de: {
            subject: 'Information über der Aufladung',
            message:
                'Guten Tag,\n\nbeifügend schicke ich Ihnen die Information über die Ladung bei Ihrer Firma.\n\nMit freudlichen Grüßen',
        },
        en: {
            subject: 'Loading confirmation',
            message:
                'Hello,\n\nIn attached file find you the information about loading in your company.\n\nBest regards',
        },
    },
    createDeliveryConfirmation: {
        cs: {
            subject: 'Informace k vykládce',
            message:
                'Dobrý den,\n\nv příloze Vám posílám avizaci příjezdu auta, které u Vás ve firmě bude vykládat.\n\nS přáním pěkného dne',
        },
        de: {
            subject: 'Information über Entladung',
            message:
                'Guten Tag,\n\nbeifügend schicke ich Ihnen die Information über die Ausladung bei Ihrer Firma.\n\nMit freudlichen Grüßen',
        },
        en: {
            subject: 'Delivery information',
            message:
                'Hello,\n\nIn attached file find you the iformation about delivery in your company.\n\nBest regards',
        },
    },
    neutralization: {
        cs: {
            subject: 'Informace k neutralizaci',
            message:
                'Dobrý den,\n\nv příloze Vám posílám informace a pokyny k neutralizaci.\n\nDěkuji\nS přáním pěkného dne',
        },
        de: {
            subject: 'Genaue Adresse und Informationen',
            message:
                'Guten Tag,\n\nbeifügend schicke ich Ihnen die Instruktionen und genaue Adresse der Ausladung.\n\nVielen Dank\nMit freudlichen Grüßen',
        },
        en: {
            subject: 'Exact adress and instructions',
            message:
                'Hello,\n\nIn attached file find you the instructions and exact adress of delivery.\n\nThank you\nBest regards',
        },
    },
};

/**
 * Universal function to find language in different ways.
 * @param value countryCode (CZ), lang (cs), languageId (36) or languageText (čeština) .
 * @return object with other language data.
 */

export function universalLanguageFinder(value: string): universalLanguage | undefined {
    const langs: universalLanguage[] = [
        { countryCode: ['CZ', 'SK'], lang: 'cs', languageId: 36, languageText: 'čeština' },
        { countryCode: ['DE', 'AT'], lang: 'de', languageId: 52, languageText: 'němčina' },
        { countryCode: ['EN'], lang: 'en', languageId: 41, languageText: 'angličtina' },
    ];

    return langs.find(
        (lang) => lang.countryCode.includes(value) || String(lang.languageId) === value || lang.languageText === value,
    );
}

/**
 * Universal way to get language for dispatcher.
 * @param dispatcherLanguageId languageId of dispatcher.
 * @param carrierCountryCode countryCode of carrier.
 * @return language in "lang" format.
 */

export function getDispatcherLanguage({
    dispatcherLanguageId,
    carrierCountryCode,
}: {
    dispatcherLanguageId?: number;
    carrierCountryCode?: keyof typeof Countries;
}): 'cs' | 'de' | 'en' {
    //  1. try to get the lang based on dispatchers's languageId
    if (dispatcherLanguageId) {
        const lang = universalLanguageFinder(String(dispatcherLanguageId));
        if (lang) return lang.lang;
    }
    // 2. otherwise try to get the lang based on carrier's countryCode
    if (carrierCountryCode && ['CZ', 'SK', 'DE', 'AT'].includes(carrierCountryCode)) {
        // 3. if the countryCode is CZ, SK, DE or AT - return the based language
        const lang = universalLanguageFinder(carrierCountryCode);
        if (lang) return lang.lang;
    }
    // 4. if the countryCode is not CZ, SK, DE or AT - return 'en'
    // 5. if country is not specified, return 'cs'
    return carrierCountryCode ? 'en' : 'cs';
}

/**
 * Format the data from MapBox response.
 * @param res first feature of response, if exist
 * @return object with address info.
 */

export function formatLocationDataFromApi(res: GeocoderSearchResult) {
    const placeData = res.context;
    const countryData = placeData.find((item) => item.id.split('.')[0] === 'country');

    const getCityValue = (GeocoderSearchResult: GeocoderSearchResult) => {
        const type = GeocoderSearchResult.place_type[0];
        if (type === GeocoderSearchResultType.Address) {
            return GeocoderSearchResult.context.find((item) => item.id.split('.')[0] === 'place')?.text || '';
        }
        if (type === GeocoderSearchResultType.Place) {
            return GeocoderSearchResult.text || '';
        }
        return '';
    };

    const transformedResult = {
        city: getCityValue(res),
        country: Countries[countryData?.short_code?.toUpperCase() as keyof typeof Countries],
        countryCode: countryData?.short_code?.toUpperCase() as keyof typeof Countries,
        postalCode: placeData.find((item) => item.id.split('.')[0] === 'postcode')?.text || '',
        latitude: res.geometry.coordinates[1],
        longitude: res.geometry.coordinates[0],
    };

    return transformedResult;
}

/**
 * Disable scrolling when on input with number type
 */

export const numberInputOnWheelPreventChange = (e: React.WheelEvent<HTMLInputElement>) => {
    // Prevent the input value change;
    'blur' in e.target && typeof e.target.blur === 'function' && e.target.blur();

    // Prevent the page/container scrolling
    e.stopPropagation();

    // Refocus immediately, on the next tick (after the current function is done)
    setTimeout(() => {
        'focus' in e.target && typeof e.target.focus === 'function' && e.target.focus();
    }, 0);
};

export type ITransformedTableLocation = {
    originalIndex: number;
    location_id: number;
    company: string | null;
    countryCode: string | null;
    postalCode: string | null;
    city: string | null;
    street: string | null;
    routeDirection: string | null;
    contactPerson: string | null;
};

// table has another fields than fetched addresses (locations)
export function transformAddressesIncomingData(data: TCustomerForm['locations'], t: T): ITransformedTableLocation[] {
    const newData: ITransformedTableLocation[] = data.map((address, index) => {
        // address can have routeDirection = loading or discharge
        // if both loading and discharge, use "Nakládka, Vykládka"
        // if only some of them, use "Nakládka" or "Vykládka"
        // if nothing of them, use "—"
        const routeDirection =
            address.loading && address.discharge
                ? t('customers.form.locations.routeDirection.loading') +
                  ', ' +
                  t('customers.form.locations.routeDirection.discharge')
                : (address.loading && t('customers.form.locations.routeDirection.loading')) ||
                  (address.discharge && t('customers.form.locations.routeDirection.discharge')) ||
                  '—';

        return {
            location_id: address.locationId || 0,
            company: address.company || '—',
            countryCode: address.countryCode || null,
            postalCode: address.postalCode || '—',
            city: address.city || '—',
            street: address.street || '—',
            routeDirection,
            contactPerson: createFullName(address.firstName, address.lastName),
            originalIndex: index,
        };
    });
    return newData;
}

interface ITableSortingFilteringPaginationParams<T = Record<string, any>> {
    data: T[];
    treatAsDate?: string[];
    queryParams: QueryParams<Record<string, any>>;
    paginationParams: PaginationParams | null;
}
/**
 * filter, sort and paginate tables with no BE.
 * @param data array of objects.
 * @param queryParam filter parameters.
 * @param paginationParams pagination parameters.
 * @return filtered, sorted and paginated result & total sorted items count
 */
export function tableSortingFilteringPagination<T>({
    data,
    queryParams,
    paginationParams,
    treatAsDate = [],
}: ITableSortingFilteringPaginationParams): {
    result: T[];
    sortedCount: number;
    filtered: T[];
    sorted: T[];
} {
    const filtered = data.filter((row) => {
        for (const [key, value] of Object.entries(queryParams)) {
            if (key === 'sort') continue;

            // if min value specified
            if (key.split('_')[1] === 'gte') {
                if (treatAsDate.includes(key.split('_')[0])) {
                    if (timestamp(row[key.split('_')[0]])?.isBefore(timestamp(value), 'days')) return false;
                    continue;
                }
                if (row[key.split('_')[0]] < Number(value)) return false;
                continue;
            }
            // if max value specified
            if (key.split('_')[1] === 'lte') {
                if (treatAsDate.includes(key.split('_')[0])) {
                    if (timestamp(row[key.split('_')[0]])?.isAfter(timestamp(value), 'days')) return false;
                    continue;
                }
                if (row[key.split('_')[0]] > Number(value)) return false;
                continue;
            }
            const actualValue = (row[key] as string).toString().toLowerCase().trim().replaceAll(' ', '');
            const searchValue = value.toLowerCase().trim().replaceAll(' ', '');
            if (!actualValue.includes(searchValue)) return false;
        }
        return true;
    });

    const sorted = filtered.sort((a: Record<string, any>, b: Record<string, any>) => {
        if (!queryParams.sort) return -1;

        const [sortBy, sortDir] = queryParams.sort.split(':');
        // sort strings
        if (typeof a[sortBy] === 'string') {
            const aVal = (a[sortBy] as string).toLowerCase().trim().replaceAll(' ', '');
            const bVal = (b[sortBy] as string).toLowerCase().trim().replaceAll(' ', '');
            if (sortDir === 'asc') {
                return aVal.localeCompare(bVal);
            } else {
                return bVal.localeCompare(aVal);
            }
        }

        // sort numbers
        const aVal = Number(a[sortBy]) || 0;
        const bVal = Number(b[sortBy]) || 0;
        return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    });

    if (!paginationParams)
        return { result: sorted as T[], sortedCount: sorted.length, filtered: filtered as T[], sorted: sorted as T[] };
    const paginated = sorted.slice(paginationParams.offset, paginationParams.limit + paginationParams.offset);
    return { result: paginated as T[], sortedCount: sorted.length, filtered: filtered as T[], sorted: sorted as T[] };
}

/**
 * format date in human-readable in user's language.
 * @param date timestamp number or Date.
 * @return formatted date in string.
 */
export function getFormattedDate(date: number | Date): string {
    const locales: { [key: string]: Locale } = {
        cs: cs,
        de: de,
    };

    /* if date is more than 2 days past, use "2.1." instead of "yesterday" */
    if (differenceInDays(new Date(), date) > 1) {
        const isCurrentYear = new Date(date).getFullYear() === new Date().getFullYear();

        return isCurrentYear ? format(date, 'd.M.') : format(date, 'd.M.yyyy');
    }

    const formattedDate = formatRelative(date, new Date(), {
        locale: i18n.language in locales ? locales[i18n.language] : undefined,
    }).split(' ');

    // remove the hours ("in 14:44")
    const indexOfHours = formattedDate.findIndex((part) => part.includes(':'));

    // EXAMPLE: "last monday at 12:00 PM" -> ["last", "monday", "at", "12:00", "PM"] -> ["last", "monday"] -> "last monday"
    return formattedDate.slice(0, indexOfHours - 1).join(' ');
}

/**
 * get number is negative.
 * @param number number to check.
 * @return boolean result.
 */
export function isNegative(number: number): boolean {
    return number < 0;
}
